import { useTranslation } from "react-i18next";
import { HistoricContainer2, MainMessagesContainer2 } from "./styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { IMessage } from "../types";
import { MessageList } from "../MessageList";
import { TextAreaComponent } from "../InputSection";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { actionDisplayNotification } from "../../../redux/notificationSlice";
import TagManager from "react-gtm-module";
import "./index.css";
import { actionSelectChat, actionSetInputText, selectorChatId } from "../../../redux/chatSlice";
import EmptyState from "../EmptyState";
import { jwtDecode } from "jwt-decode";
import type { ChatCreateParams } from "../../../pages/Home";
import { selectorProjectById } from "../../../redux/projectSlice";
import { throttle } from "lodash";
import { useSendMessage } from "./hooks/useSendMessage";
import { useChatState } from "./hooks/useChatState";

interface CompleteChatProps {
  project_id?: number;
  onChatCreate: (chatUpdate: ChatCreateParams) => void;
}

interface IUserData {
  email: string;
}

const buildUserContent = (prompt: string, file?: File) => {
  if (file && file.type.startsWith("image/")) {
    return [
      { type: "text", text: prompt },
      {
        type: "image_url",
        image_url: {
          url: URL.createObjectURL(file),
        },
      },
    ];
  }
  return prompt;
};

//Função para enviar eventos para o GTM
const trackGTMEvent = (eventName: string, eventData: Record<string, unknown>) => {
  const jwt = localStorage.getItem("jwt") as string;
  const decodedToken = jwtDecode(jwt) as IUserData;
  TagManager.dataLayer({
    dataLayer: {
      event: eventName,
      ...eventData,
      username: decodedToken.email.split("@")[0],
      timestamp: new Date().toISOString(),
    },
  });
};

const CompleteChat: React.FC<CompleteChatProps> = ({ project_id, onChatCreate }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedChat = useAppSelector(selectorChatId);
  const selectedProject = useAppSelector(selectorProjectById);

  const bottomRef = useRef<HTMLInputElement>(null);

  const [isBlockedMessage, setisBlockedMessage] = useState(false);

  const { getOrCreateChatId, messageList, showEmptyState, dispatchMessage } = useChatState(
    dispatch,
    onChatCreate,
    selectedChat,
    selectedProject,
    project_id,
  );

  const { sendMessageStream, response, isStreaming } = useSendMessage({
    onMessageComplete: (message) => {
      dispatchMessage({ type: "ADD_MESSAGE", payload: message });
    },
  });

  const handleSendMessage = async (currentMessage: string, imageFile?: File) => {
    if (!project_id || currentMessage === "") {
      dispatch(
        actionDisplayNotification({
          messages: [t("pages.chat.components.notifications.waitForAIResponseToSend")],
          severity: "warning",
        }),
      );
      return;
    }

    const chatId = await getOrCreateChatId(currentMessage);
    if (!chatId) {
      return;
    }

    // Evento de criacao de chat
    trackGTMEvent("newMessageSend", {
      chatId: chatId,
      projectId: project_id,
      message: currentMessage,
    });

    const welcomeMessage: IMessage = {
      role: "assistant",
      content: selectedProject?.welcome_message as string,
      date_time: new Date().toISOString(),
    };

    const newUserMessage: IMessage = {
      role: "user",
      date_time: new Date().toISOString(),
      content: buildUserContent(currentMessage, imageFile),
    };

    // Reset na caixa de input
    dispatch(actionSetInputText(""));

    // Adicionando mensagens no historico
    dispatchMessage({
      type: "ADD_MESSAGE_WITH_WELCOME",
      payload: { welcomeMessage, newUserMessage },
    });

    try {
      setisBlockedMessage(true);
      await sendMessageStream(project_id, chatId, currentMessage, imageFile);
    } catch (error) {
      dispatchMessage({ type: "REMOVE_LAST" });
      dispatch(
        actionDisplayNotification({
          messages: [t("pages.chat.components.erro")],
        }),
      );
    }

    setisBlockedMessage(false);
    dispatch(
      actionSelectChat({
        projectId: project_id,
        selectedChatId: chatId,
        emptyChat: false,
        firstOpenedChat: false,
      }),
    );
  };

  const handleNewChatRequest = async () => {
    if (!project_id) {
      dispatch(
        actionDisplayNotification({
          messages: [t("pages.chat.components.notifications.waitForAIResponseToSend")],
          severity: "warning",
        }),
      );
      return;
    }

    const chatId = await getOrCreateChatId(undefined);
    if (!chatId) {
      return;
    }
    dispatch(
      actionSelectChat({
        projectId: project_id,
        selectedChatId: chatId,
        emptyChat: false,
        firstOpenedChat: false,
      }),
    );
  };

  const debounceScroll = useCallback(
    throttle(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 400),
    [],
  );

  // Scrolls to bottom every time messageList is modified
  useEffect(debounceScroll, [messageList]);

  const messageHistory = isStreaming ? [...messageList, response] : messageList;

  return (
    <>
      {showEmptyState ? (
        <EmptyState />
      ) : (
        <HistoricContainer2>
          <MainMessagesContainer2>
            <MessageList messages={messageHistory} />
            <div ref={bottomRef} />
          </MainMessagesContainer2>
        </HistoricContainer2>
      )}

      <TextAreaComponent
        // key={selectedChat?.selectedChatId}
        handleSendMessage={handleSendMessage}
        isBlockedMessage={isBlockedMessage}
        onNewChatRequest={handleNewChatRequest}
      />
    </>
  );
};

export default CompleteChat;
